/*
 *Module for topic examples in the topic page
 */

define([
  "marionette",
  "messaging/messenger",
  "modules/topicsSplit/topic/topicExamples/topicExamplesView",
  "modules/topicsSplit/topic/topicExampleModal/topicExampleModalModule",
  "modules/topicsSplit/topic/topicExampleGeneratorModal/topicExampleGeneratorModalModule",
  "jquery",
], function (
  Marionette,
  Messenger,
  TopicExamplesView,
  TopicExampleModalModule,
  TopicExampleGeneratorModalModule,
  $,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.region = options.region;
      this.topic = options.topic;
      this.name = "topicExamples";
      this.readOnly = !!options.readOnly;
    },
    show: function (examples) {
      this.examples = examples;
      if (this.readOnly) {
        examples.allowDeleteExamples = false;
        examples.allowEditExamples = false;
      } else {
        examples.allowDeleteExamples = Messenger.reqres.request("can:delete:topicExamples");
        examples.allowEditExamples = Messenger.reqres.request("can:edit:topicExamples");
      }
      this.languages = Messenger.reqres.request("kb:languages");
      this.topic.allowEditExamples = examples.allowEditExamples;
      this.kb = Messenger.reqres.request("active:kb:model");
      this.topic.allowGenerateExamples =
        this.examples.selectedEnglish && this.kb.get("exampleGeneration");
      this.view = new TopicExamplesView.topicExamplesView({
        model: this.topic,
        collection: this.examples,
      });
      this.examples.localesHasExample = [];
      this.examples.localesNoExample = [];

      //edit example event
      this.view.on(
        "itemview:edit:example",
        function (itemView) {
          if (examples.allowEditExamples) {
            Messenger.reqres
              .request("topic:saveIfNew")
              .then(() => this._loadEditorModal(itemView.model));
          }
        },
        this,
      );
      //delete example event
      this.view.on(
        "itemview:delete:example",
        function (itemView) {
          if (examples.allowDeleteExamples) {
            Messenger.yesNoDialog(
              "topic.example.dialog.delete",
              {
                yes: function () {
                  Messenger.vent.trigger("track:interaction", {
                    action: "Delete Topic Example",
                    id: itemView.model.id,
                    idType: "Topic Example",
                    object: "Topic Example",
                  });
                  itemView.model.deleteModel();
                },
              },
              this,
            );
          }
        },
        this,
      );
      //Add example event
      this.view.on(
        "add:example",
        function () {
          if (examples.allowEditExamples) {
            examples.saveToAdd = true;
            Messenger.reqres
              .request("topic:saveIfNew")
              .then(() => {
                const example = examples.addExample();
                this._loadEditorModal(example);
              })
              .finally(() => (examples.saveToAdd = false));
          }
        },
        this,
      );

      // generate examples from oldest authored example or typed in user utterance
      this.view.on(
        "generate:examples",
        function () {
          if (examples.allowEditExamples) {
            examples.saveToAdd = true;
            Messenger.reqres
              .request("topic:saveIfNew")
              .then(() => {
                // get the oldest authored example if exists
                const example = this._findOldestAuthoredExample(this.examples.selectedLocaleId);
                // the example model passed here should only be used in text editor to
                // generate examples and should not be used to modify the example model.
                this._loadExampleGeneratorModal(example);
              })
              .finally(() => (examples.saveToAdd = false));
          }
        },
        this,
      );

      //Event for when a learner transcript is dragged and dropped into the examples area
      this.view.on(
        "dropped:example",
        function () {
          if (examples.allowEditExamples) {
            //get the dragged model
            const learnerTranscript = Messenger.reqres.request("dragged:model");
            const learnerConfig = Messenger.reqres.request("learner:config:settings");
            this.addTranscriptExample(learnerTranscript, learnerConfig.localeId);
          }
        },
        this,
      );

      this.view.on(
        "change:locale",
        function () {
          const localeId = this.view.getLocaleId();
          this._updateSelectedLocale(localeId);
          Messenger.vent.trigger("change:example:locale", localeId);
        },
        this,
      );

      this.languages.onSynced(function () {
        this._updateSelectedLocale(Messenger.reqres.request("default:response:language"));
        this._prepareLocales();
        this.showRegion(this.view, { loading: true });
        this._setUpReqRes();
      }, this);
    },
    _updateSelectedLocale: function (localeId) {
      this.examples.selectedLocaleId = localeId;
      this.examples.selectedEnglish = Messenger.reqres.request("kb:language:is:english", +localeId);
      this.topic.allowGenerateExamples =
        this.examples.selectedEnglish && this.kb.get("exampleGeneration");
    },
    _findOldestAuthoredExample: function (selectedLocale) {
      let oldestExample;
      if (this.examples && this.examples.length > 0 && this.examples.selectedEnglish) {
        let oldestTimestamp = new Date();
        this.examples.each((example) => {
          const isExampleEnglish = Messenger.reqres.request(
            "kb:language:is:english",
            +example.get("localeId"),
          );
          if (
            isExampleEnglish &&
            example.get("localeId") == selectedLocale &&
            example.get("changeTimestamp") &&
            example.get("changeTimestamp") < oldestTimestamp
          ) {
            oldestTimestamp = example.get("changeTimestamp");
            oldestExample = example;
          }
        });
        if (!oldestExample && this.examples.selectedEnglish) {
          // there is no example found in the selected locale, pick a random example from any english locale
          // only return english example, since example generation is only supported for english locales
          for (let i = 0; i < this.examples.length; i++) {
            const example = this.examples.at(i);
            const isExampleEnglish = Messenger.reqres.request(
              "kb:language:is:english",
              +example.get("localeId"),
            );
            if (isExampleEnglish) {
              return example;
            }
          }
        }
      }
      // this is likely a new topic with no examples authored yet.
      return oldestExample;
    },
    //divide locales into 'has Example' and 'no example' list to be shown as a dropdown
    _prepareLocales: function () {
      this.examples.onSynced(function () {
        const coll = this.topic;
        coll.hasExampleLabel = $.t("languages.hasExampleLabel");
        coll.noExampleLabel = $.t("languages.noExampleLabel");
        const localesHasExample = [];
        const localesNoExample = [];
        this.languages.each(function (language) {
          let hasExample = false;
          this.examples.each(function (example) {
            if (language.get("id") === example.get("localeId") && !hasExample) {
              hasExample = true;
            }
          }, this);
          if (hasExample) {
            localesHasExample.push({ localeId: language.get("id"), name: language.get("name") });
          } else {
            localesNoExample.push({ localeId: language.get("id"), name: language.get("name") });
          }
        }, this);
        coll.localesHasExample = localesHasExample;
        coll.localesNoExample = localesNoExample;
        this.view.render();
      }, this);
    },
    //add a new example from a transcript and load it in the example editor
    addTranscriptExample: function (transcript, localeId) {
      //make sure examples are synced first
      this.examples.onSynced(function () {
        this.examples.saveToAdd = true;
        Messenger.reqres
          .request("topic:saveIfNew")
          .then(() => {
            //create a new example, pass the transcript and open the editor modal
            const example = this.examples.addExample();
            this._loadEditorModal(example, transcript, localeId);
            // delete clustering job since the results are added to a topic and is now old.
            Messenger.vent.trigger("delete:clustering:jobs");
          })
          .finally(() => (this.examples.saveToAdd = false));
      }, this);
    },
    emptyError: function (errorMessage) {
      this.view.emptyError(errorMessage);
    },
    _loadEditorModal: function (model, learnerTranscript, localeId) {
      // Do not call Messenger.save() to save the Topic, that was already handled before calling this function
      const exampleBundle = {
        example: model,
        learnerTranscript: learnerTranscript,
      };
      let selectedLocale = this.view.getLocaleId();
      if (localeId) {
        selectedLocale = localeId;
      }
      let trackInteraction;
      if (model.isNew()) {
        trackInteraction = {
          action: "Add Topic Example",
          id: this.topic.id,
          idType: "Topic",
          object: "Topic",
        };
      } else {
        trackInteraction = {
          action: "Edit Topic Example",
          id: [this.topic.id, model.id],
          idType: ["Topic", "Topic Example"],
          object: "Topic Example",
        };
      }

      //load example modal to edit this example
      Messenger.vent.trigger(
        "show:modal",
        TopicExampleModalModule,
        {
          localeId: selectedLocale,
        },
        exampleBundle,
        {
          title: "topic.example.title",
          closeButton: false,
          closeOnBackgroundClick: false,
          trackInteraction: trackInteraction,
        },
      );
      //rerender example once the modal is closed
      Messenger.vent.once(
        "close:modal",
        function () {
          if (!model.isEmpty()) {
            const localeId = this.view.getLocaleId();
            this.view.render();
            this.view.setSelectedLocale(localeId);
          }
        },
        this,
      );
    },
    _loadExampleGeneratorModal: function (model, localeId) {
      // Do not call Messenger.save() to save the Topic, that was already handled before calling this function
      let selectedLocale = this.view.getLocaleId();
      if (localeId) {
        selectedLocale = localeId;
      }
      // Note: example model can be undefined if the topic has no english examples
      const exampleBundle = {
        example: model,
        examplesCollection: this.examples,
      };

      const trackInteraction = {
        action: "Generate Example Modal",
        id: this.topic.id,
        idType: "Topic",
        object: "Topic",
      };

      //load example modal to edit this example
      Messenger.vent.trigger(
        "show:modal",
        TopicExampleGeneratorModalModule,
        {
          localeId: selectedLocale,
          topicId: this.topic.id,
        },
        exampleBundle,
        {
          title: "topic.examples.generator.title",
          closeButton: false,
          closeOnBackgroundClick: false,
          trackInteraction: trackInteraction,
        },
      );
      //rerender example once the modal is closed
      Messenger.vent.once(
        "close:modal",
        function () {
          if (!model.isEmpty()) {
            this.view.render();
          }
        },
        this,
      );
    },
    _setUpReqRes: function () {
      Messenger.reqres.setHandler(
        "current:topic:examples",
        function () {
          return this.examples;
        },
        this,
      );
    },
  });
});
